import React, { useEffect, useRef, useState } from 'react'
import { ChevronUp, Eye, FolderOpen, LayoutGrid, Paperclip, PenLine, X } from 'lucide-react'
import { cn } from 'tailwind-v4/lib/utils'
import {
  AGENT_Z,
  AGENT_Z_BACKDROP,
  CAPTION_CLASSES,
  CAPTION_LINGER_MS,
  COMPOSER_CLASSES,
  LAYER_CLASSES,
  DROP_TARGET_CLASSES,
  PANEL_BAR_CLASSES,
  PANEL_ROW_CLASSES,
  STACK_CLASSES,
  blocksPage,
  composerOverlap,
  panelClasses,
  stackWidth,
  type Occupancy,
} from './occupancy'
import { ChoosePart } from './parts/choose-part'
import { Transcript, Thinking, type TranscriptTurn } from './parts/transcript'
import type { AgentPartState, ChooseOption } from './parts/types'
import { PANEL_LABEL, type OpenPanel, type PanelId } from './panels/types'
import { VoiceOrb, type OrbState } from './voice-orb'
import { useFileDrop } from './use-file-drop'
import { DiagramStage } from './diagram/diagram-stage'
import type { DiagramScene } from './diagram/scene'

/**
 * The agent surface: one layer over the whole app.
 *
 * NOT A CHAT WINDOW. A chat window is a box you talk to about the
 * product. This is a layer the agent composes ON TOP of the product: an
 * orb, a composer, and surfaces that come and go above them. It mounts
 * above the router so a navigation cannot unmount a conversation and so
 * the agent can change the page underneath itself.
 *
 * INERT BY DEFAULT, which is the decision everything else follows from.
 * The container is `pointer-events-none` and only painted surfaces turn
 * it back on, so the agent can sit over a bookings list and somebody can
 * still click a row straight through it.
 *
 * THE ORB NEVER MOVES. It lives in the composer, centred at the bottom,
 * and everything else appears above it in the same column. In `full` the
 * panel's bottom edge runs BEHIND the composer, so the two read as one
 * object with a handle rather than two rectangles with a gap.
 *
 * ONE THREAD, TYPED AND SPOKEN. The transcript is the same conversation
 * whether it arrived as speech, as text, or as a file dropped on it, and
 * the microphone stays open while you type. That is the whole point of
 * the surface: you can paste a config and say "ignore the second one" in
 * the same breath.
 */
/**
 * The instrument buttons, in a fixed order.
 *
 * Fixed because a row of controls that reorders itself is a row nobody
 * builds muscle memory for. `context` first, because it is the one that
 * answers "what do you actually know".
 */
const PANEL_ORDER: { id: PanelId; Icon: typeof Eye }[] = [
  { id: 'context', Icon: Eye },
  { id: 'pages', Icon: LayoutGrid },
  { id: 'workspace', Icon: FolderOpen },
]

export function AgentSurface({
  orbState,
  level,
  caption,
  part,
  turns,
  expanded,
  onToggleExpanded,
  thinking,
  muted,
  onToggleMute,
  onSend,
  onChoose,
  onFiles,
  pending,
  onDropPending,
  panels,
  onTogglePanel,
  renderPanel,
  diagram,
  onCloseDiagram,
  onSaveDiagram,
  onNewDiagram,
  savingDiagram,
}: {
  orbState: OrbState
  level: number
  caption?: string
  part: AgentPartState | null
  turns: TranscriptTurn[]
  expanded: boolean
  onToggleExpanded: () => void
  /**
   * The agent is working.
   *
   * It runs tools for several seconds before any text, so without this
   * the surface looks identical to one that did not hear you. The same
   * complaint the spoken half had, answered the same way.
   */
  thinking: boolean
  muted: boolean
  onToggleMute: () => void
  onSend: (text: string) => void
  onChoose: (option: ChooseOption) => void
  /** Files chosen or dropped, before they go up with a turn. */
  onFiles: (files: File[]) => void
  pending: { name: string; kind: 'image' | 'file' }[]
  onDropPending: (name: string) => void
  /** Which instruments are open, newest last. */
  panels: OpenPanel[]
  onTogglePanel: (id: PanelId) => void
  /** The dock owns what goes in each one; this only places them. */
  renderPanel: (id: PanelId) => React.ReactNode
  /** Whatever is on the canvas: drawn, started blank, or opened. */
  diagram: DiagramScene | null
  onCloseDiagram: () => void
  onSaveDiagram?: (filename: string, contents: string) => void
  /** Start a blank one. A quick action, not something to ask the agent for. */
  onNewDiagram: () => void
  savingDiagram?: 'saving' | 'saved' | null
}) {
  /**
   * `full` beats whatever the agent asked for.
   *
   * Somebody who has opened the conversation is reading it, and shrinking
   * it back to a question panel because a tool returned would take the
   * screen off them mid-sentence.
   */
  /**
   * `full` dims and takes the screen, and that is only right when
   * somebody has deliberately made the instruments big. Panels on their
   * own never dim: the whole reason they are worth the complexity is that
   * you read them while working.
   */
  /**
   * A DIAGRAM OUTRANKS EVERYTHING. It is the only thing the agent puts up
   * that somebody is expected to look at and work on rather than read
   * past, so it takes the screen until they close it: shrinking it back
   * because a tool returned would take the drawing off them mid-thought.
   */
  const mode: Occupancy = diagram
    ? 'canvas'
    : expanded
      ? 'full'
      : (part?.mode ?? 'orb')
  /**
   * Panels do NOT dim the page.
   *
   * The whole reason they are worth the complexity is that you read them
   * while working, so `blocksPage` stays false with three of them open.
   * Only `stage` and the full transcript take the screen.
   */
  const showPanels = panels.length > 0

  /**
   * Drop files anywhere on the page.
   *
   * Listened for on the WINDOW, because this layer is
   * `pointer-events: none` and that drops drag events too: a file let go
   * over the layer would otherwise land on the page behind and the browser
   * would navigate away to render it. See `use-file-drop`.
   */
  const dragging = useFileDrop(true, onFiles)
  const [draft, setDraft] = useState('')

  /**
   * The caption fades out on its own.
   *
   * Keyed on the TEXT rather than on a tick, so a new sentence brings it
   * back and re-starts the clock, and the same sentence does not keep
   * resetting it on every re-render.
   */
  const [captionShown, setCaptionShown] = useState(true)
  useEffect(() => {
    if (!caption) return
    setCaptionShown(true)
    const timer = window.setTimeout(() => {
      setCaptionShown(false)
    }, CAPTION_LINGER_MS)
    return () => {
      window.clearTimeout(timer)
    }
  }, [caption])
  const picker = useRef<HTMLInputElement | null>(null)
  const foot = useRef<HTMLDivElement | null>(null)

  // Pinned to the newest, like every other conversation anybody has used.
  useEffect(() => {
    if (!expanded) return
    foot.current?.scrollIntoView({ block: 'end' })
  }, [expanded, turns.length])

  const send = () => {
    const text = draft.trim()
    if (text === '' && pending.length === 0) return
    setDraft('')
    onSend(text)
  }

  return (
    <div className={LAYER_CLASSES} style={{ zIndex: AGENT_Z }}>
      {blocksPage(mode) ? (
        <button
          type="button"
          aria-label="Close the conversation"
          onClick={expanded ? onToggleExpanded : undefined}
          className="pointer-events-auto fixed inset-0 bg-foreground/20"
          style={{ zIndex: AGENT_Z_BACKDROP }}
        />
      ) : null}

      {dragging ? (
        <div className={DROP_TARGET_CLASSES} style={{ zIndex: AGENT_Z }}>
          <p className="text-sm font-medium text-foreground">
            Drop them anywhere
          </p>
        </div>
      ) : null}

      <div
        className={cn(STACK_CLASSES, stackWidth(mode, panels.length))}
        style={{ zIndex: AGENT_Z }}
      >
        {showPanels && !diagram ? (
          <div className={PANEL_ROW_CLASSES}>
            {panels.map((panel) => (
              <React.Fragment key={panel.id}>
                {renderPanel(panel.id)}
              </React.Fragment>
            ))}
          </div>
        ) : null}

        {diagram ? (
          <div className={panelClasses('canvas')}>
            <DiagramStage
              scene={diagram}
              onClose={onCloseDiagram}
              onSave={onSaveDiagram}
              saving={savingDiagram}
            />
          </div>
        ) : null}

        {/* THE CONVERSATION IS NOT AN INSTRUMENT. It is the thing
            itself, so the chevron opens it rather than it queueing behind
            a button alongside a file browser. Its bottom edge runs behind
            the composer, so the two read as one object. */}
        {expanded && !diagram ? (
          <div className={cn(panelClasses('full'), 'flex flex-col gap-3')}>
            {part ? (
              <div key={part.seq} className="shrink-0">
                <ChoosePart part={part.part} onChoose={onChoose} />
              </div>
            ) : null}
            <Transcript turns={turns} thinking={thinking} />
            <div ref={foot} />
          </div>
        ) : part && !diagram ? (
          <div key={part.seq} className={panelClasses(part.mode)}>
            <ChoosePart part={part.part} onChoose={onChoose} />
          </div>
        ) : null}

        {/* Working, with the conversation shut. Over the composer so it is
            where the answer will appear, and it replaces the caption
            rather than stacking with it. */}
        {thinking && !expanded ? (
          <div className={cn(CAPTION_CLASSES, 'px-4 py-2')}>
            <Thinking />
          </div>
        ) : caption && !part && !expanded && captionShown ? (
          /* Pressing the last thing it said opens the rest of it, which is
             the only thing anybody would expect pressing it to do. */
          <button
            type="button"
            onClick={onToggleExpanded}
            title="Open the conversation"
            className={cn(CAPTION_CLASSES, 'line-clamp-2 max-w-full')}
          >
            {caption}
          </button>
        ) : null}

        <div className={cn(COMPOSER_CLASSES, composerOverlap(mode))}>
          {pending.length > 0 ? (
            <div className="flex w-full flex-wrap gap-1.5 pb-1">
              {pending.map((file) => (
                <span
                  key={file.name}
                  className="flex items-center gap-1 rounded-lg border border-black/10 px-2 py-1 text-xs text-muted-foreground dark:border-white/10"
                >
                  {file.name}
                  <button
                    type="button"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => {
                      onDropPending(file.name)
                    }}
                  >
                    <X className="size-3" />
                  </button>
                </span>
              ))}
            </div>
          ) : null}

          {/* THE INSTRUMENTS, ON THEIR OWN ROW. Inline with the input they
              squeezed it: five named buttons, a paperclip and an orb left
              the placeholder wrapping onto two lines. A row of its own
              costs 28px and gives the input its width back. */}
          <div className={PANEL_BAR_CLASSES}>
            <button
              type="button"
              aria-label={
                expanded ? 'Close the conversation' : 'Open the conversation'
              }
              aria-expanded={expanded}
              onClick={onToggleExpanded}
              className="shrink-0 rounded-full p-1.5 text-muted-foreground hover:text-foreground"
            >
              <ChevronUp
                className={cn('size-4 transition-transform', expanded && 'rotate-180')}
              />
            </button>

            {/* START ONE YOURSELF. Drawing should not require asking an
                agent for permission: most of the time somebody wants a
                canvas to think on, and the agent joining in afterwards is
                the bonus rather than the entry fee. */}
            <button
              type="button"
              aria-label="Start a diagram"
              title="Start a diagram"
              onClick={onNewDiagram}
              className={cn(
                'flex shrink-0 items-center gap-1.5 rounded-full px-2 py-1.5 text-xs',
                diagram
                  ? 'bg-black/5 text-foreground dark:bg-white/10'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <PenLine className="size-4 shrink-0" />
              <span className="hidden whitespace-nowrap md:inline">Draw</span>
            </button>

            {PANEL_ORDER.map(({ id, Icon }) => {
              const on = panels.some((panel) => panel.id === id)
              return (
                <button
                  key={id}
                  type="button"
                  aria-label={PANEL_LABEL[id]}
                  aria-pressed={on}
                  title={PANEL_LABEL[id]}
                  onClick={() => {
                    onTogglePanel(id)
                  }}
                  className={cn(
                    'flex shrink-0 items-center gap-1.5 rounded-full px-2 py-1.5 text-xs',
                    on
                      ? 'bg-black/5 text-foreground dark:bg-white/10'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <Icon className="size-4 shrink-0" />
                  {/* Named, because five bare icons is not something
                      anybody finds: "how do I get to the workspace" was
                      asked of a button already on screen. */}
                  <span className="hidden whitespace-nowrap md:inline">
                    {PANEL_LABEL[id]}
                  </span>
                </button>
              )
            })}
          </div>

          <div className="flex w-full items-end gap-1.5">
            <textarea
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value)
              }}
              onKeyDown={(event) => {
                // Enter sends, shift+enter is a new line. A config pasted
                // in arrives with its own newlines and must not send on
                // each one.
                if (event.key !== 'Enter' || event.shiftKey) return
                event.preventDefault()
                send()
              }}
              onPaste={(event) => {
                // A screenshot off the clipboard is the commonest image
                // anybody will give it, and cmd+V is how people send one.
                const files = [...event.clipboardData.files]
                if (files.length === 0) return
                event.preventDefault()
                onFiles(files)
              }}
              rows={1}
              placeholder="Ask, paste, or drop a file in…"
              aria-label="Ask the assistant"
              className={cn(
                'max-h-40 min-h-9 flex-1 resize-none bg-transparent px-2 py-1.5 text-sm',
                'text-foreground outline-none placeholder:text-muted-foreground',
              )}
            />

            <button
              type="button"
              aria-label="Add a file"
              onClick={() => {
                picker.current?.click()
              }}
              className="shrink-0 rounded-full p-1.5 text-muted-foreground hover:text-foreground"
            >
              <Paperclip className="size-4" />
            </button>
            <input
              ref={picker}
              type="file"
              multiple
              accept="image/*,.csv,.md,.txt,.json"
              className="hidden"
              onChange={(event) => {
                onFiles([...(event.target.files ?? [])])
                event.target.value = ''
              }}
            />

            <button
              type="button"
              onClick={onToggleMute}
              aria-label={muted ? 'Turn the microphone on' : 'Turn the microphone off'}
              aria-pressed={muted}
              className={cn(
                'shrink-0 rounded-full outline-none transition-opacity',
                'focus-visible:ring-2 focus-visible:ring-[var(--brand-hot,#ff2d87)]',
                muted && 'opacity-40',
              )}
            >
              <VoiceOrb
                state={muted ? 'idle' : orbState}
                level={muted ? 0 : level}
                className="size-10"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
